import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, Button, Spinner, Badge, Select, Input } from "@/components/ui";
import { endpoints, apiError, api, pollJob } from "@/lib/api";
import { confirmCost } from "@/lib/confirmCost";
import { Gavel, RefreshCw, Trash2, Download, Plus, ArrowRight } from "lucide-react";

const TONE = { APPROACH: "green", WATCH: "gold", "RESEARCH MORE": "navy", PASS: "red" };

async function downloadPdf(cid, name) {
  const r = await api.get(`/committee/${cid}/pdf`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = `CLARA_Committee_${name}.pdf`;
  a.click();
}

function WatchRow({ w, onRerun, onRemove, busy }) {
  const hist = (w.history || []).slice(-6);
  return (
    <Card hover={false} data-testid={`watch-item-${w.company_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-display text-xl">{w.company_name}</div>
          <div className="text-[11.5px] text-white/40">{[w.sector, w.country].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="flex items-center gap-2">
          {w.last_recommendation ? (
            <>
              <Badge tone={TONE[w.last_recommendation]} data-testid={`watch-rec-${w.company_id}`}>{w.last_recommendation}</Badge>
              {w.last_score != null && <span className="font-mono text-gold text-[13px]">{w.last_score}/100</span>}
            </>
          ) : <Badge tone="neutral">Not yet run</Badge>}
          {["queued", "running"].includes(w.status) && <span className="flex items-center gap-1 text-[11px] text-white/50" data-testid={`watch-status-${w.company_id}`}><Spinner size={11} /> {w.status}</span>}
        </div>
      </div>
      {hist.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-4" data-testid={`watch-history-${w.company_id}`}>
          {hist.map((h, i) => (
            <React.Fragment key={h.committee_id || i}>
              {i > 0 && <ArrowRight size={10} className="text-white/25" />}
              <span className={`px-2 py-1 rounded text-[10px] border ${h.changed ? "border-[#C5A059] text-gold" : "border-white/10 text-white/55"}`} title={h.at}>
                {h.recommendation} · {h.score} · {String(h.at).slice(5, 10)}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      {w.error && <div className="text-[11px] text-red-300 mt-2">Last run failed: {w.error}</div>}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" onClick={() => onRerun(w)} disabled={busy} data-testid={`watch-rerun-${w.company_id}`}><RefreshCw size={12} /> Re-run now</Button>
        {w.last_committee_id && <Button size="sm" variant="outline" onClick={() => downloadPdf(w.last_committee_id, w.company_name)} data-testid={`watch-pdf-${w.company_id}`}><Download size={12} /> Latest PDF</Button>}
        <Button size="sm" variant="ghost" onClick={() => onRemove(w)} data-testid={`watch-remove-${w.company_id}`}><Trash2 size={12} /> Remove</Button>
      </div>
    </Card>
  );
}

export default function CommitteeWatch() {
  const [items, setItems] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [pick, setPick] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => endpoints.committeeWatch().then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      endpoints.companies({ q, entity_type: "target", limit: 200 }).then((r) => setCompanies(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!items?.some((w) => ["queued", "running"].includes(w.status))) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items]);

  const add = async () => {
    if (!pick) return;
    setMsg("");
    try { await endpoints.committeeWatchAdd(pick); setPick(""); load(); } catch (e) { setMsg(apiError(e)); }
  };

  const rerun = async (w) => {
    const n = w ? 1 : items.length;
    const ok = await confirmCost({
      op: "deal_committee", units: n,
      title: w ? `Re-run Deal Committee on ${w.company_name}?` : `Re-run Deal Committee on all ${n} watched companies?`,
      items: w ? [w.company_name] : items.map((x) => x.company_name),
      llm_calls: n * 7, web_calls: n, estimated_seconds: 180,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await endpoints.committeeWatchRerun(w?.id);
      load();
      pollJob(`/jobs/${r.data.job_id}`, () => load(), 5000, 60).finally(() => { setBusy(false); load(); });
    } catch (e) { setMsg(apiError(e)); setBusy(false); }
  };

  const remove = async (w) => { await endpoints.committeeWatchRemove(w.id); load(); };
  const watched = new Set((items || []).map((w) => w.company_id));
  const anyRunning = busy || !!items?.some((w) => ["queued", "running"].includes(w.status));

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <PageHeader eyebrow="Committee Watchlist" title={<>Weekly <span className="italic text-gold">committee</span> re-runs.</>}
        description="Every Monday at 06:00 UTC Clara re-runs the Virtual Deal Committee on these companies. When a recommendation changes you get an in-app notification and an email."
        actions={items?.length ? <Button onClick={() => rerun(null)} disabled={anyRunning} data-testid="watch-rerun-all"><Gavel size={13} /> Re-run all now</Button> : null}
        testid="committee-watch-page" />
      <Card hover={false}>
        <div className="overline mb-3">Add a company ({items?.length || 0}/20)</div>
        <div className="flex flex-col md:flex-row gap-2">
          <Input placeholder="Search companies…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="watch-company-search" className="md:max-w-[220px]" />
          <Select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="watch-company-select" className="flex-1">
            <option value="">Select a company…</option>
            {companies.filter((c) => !watched.has(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Button onClick={add} disabled={!pick || (items?.length || 0) >= 20} data-testid="watch-add-btn"><Plus size={13} /> Watch</Button>
        </div>
        {msg && <div className="text-[12px] text-red-300 mt-2" data-testid="watch-msg">{msg}</div>}
      </Card>
      {!items ? <Spinner /> : !items.length ? (
        <div className="text-center text-white/40 text-sm py-10" data-testid="watch-empty">No companies watched yet. Add one above, or use “Watch Committee” in any company profile. <Link to="/companies" className="text-gold hover:underline">Browse companies</Link></div>
      ) : items.map((w) => <WatchRow key={w.id} w={w} onRerun={rerun} onRemove={remove} busy={anyRunning} />)}
    </div>
  );
}
